<template>
  <div class="configuration-item" id='row'>
    <div class="configuration-item__control">
      <CustomCheckbox
        color="cold-marine"
        :value="isSelected"
        size="s"
        @input="$emit('checked', website)"
      />
    </div>
    <div class='configuration-item__content'>
      <div class="configuration-item__content__info">
        <div class="main-info" @click="toggleOpenRow">
          <div class="main-info__sensitivity-wrapper">
            <div class='header' v-if='displayHeaders' @click.stop='headerClicked("infrastructure.tab.sensitivity.header")'>
              <span v-if='lastHeaderClicked === "infrastructure.tab.sensitivity.header"' class='material-icons-outlined direction'>
                {{ directionIcon }}
              </span>
            </div>
            <custom-tooltip class="sensitivity-tooltip" popper-class="light-tooltip">
              <div slot='buttonContent'>
                  <div class='main-info__sensitivity-wrapper__data' @click.stop="openSensitivityPopup">
                    <div class="sensitivity" :class="sensitivity"></div>
                  </div>
                </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ sensitivityTooltip }}
                </slot>
              </div>
            </custom-tooltip>
          </div>
          <div class="site">
            <div class='header' v-if='displayHeaders' @click.stop='headerClicked("infrastructure.tab.main.asset.header")'>
              {{ $t('infrastructure.tab.main.asset.header') }}
              <span v-if='lastHeaderClicked === "infrastructure.tab.main.asset.header"' class='material-icons-outlined direction'>
                {{ directionIcon }}
              </span>
            </div>
            <div class='content' :class="{ successful: !isVulnerable }">
              <v-menu
                bottom
                right
                offset-x
                :close-on-content-click='true'
                :nudge-width="100"
                rounded
              >
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    id='more'
                    class='material-icons-outlined more-btn'
                  >
                    more_vert
                  </div>
                </template>

                <v-list>
                  <v-list-item
                    v-for="(item, i) in actionsMenuOptions"
                    :key="i"
                    link
                    @click='initiateActionFromMenu(item.value)'
                    :disabled='checkIfActionDisabled(item.value)'
                  >
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                  </v-list-item>
                  <v-list-group
                    ripple
                    :value="true"
                    v-for="(additionalItem,i) in additionalActionOptions"
                    :key='`${i}_${additionalItem}`'
                  >
                    <template v-slot:activator>
                      <v-list-item-title class='list-group-title'>{{ additionalItem.title }}</v-list-item-title>
                    </template>
                    <v-list-item
                      link
                      v-for="(suppressAction, j) in additionalItem.subActions"
                      :key='`${j}_${suppressAction}`'
                      @click='initiateActionFromMenu(suppressAction.value)'
                    >
                      <v-list-item-title class='list-sub-group-item'> {{ suppressAction.title }} </v-list-item-title>
                    </v-list-item>
                  </v-list-group>
                </v-list>
              </v-menu>
              <v-progress-circular
                rotate="270"
                :size='20'
                :width='2'
                :color='progressColor'
                :value="40"
                :indeterminate='true'
                v-if='autoHijackInProgress'
              />
              <v-icon color='#4d9feb' v-else-if='hasBeenTookOver' class='shield-icon'>verified_user</v-icon>
              <custom-tooltip popper-class="light-tooltip" v-else-if='noPocSubdomain'>
                <div slot='buttonContent'>
                  <test-status-failure-icon class="no-poc-status-icon"/>
                </div>
                <div slot="content">
                  <slot name="tooltip">
                    {{ warningTooltip }}
                  </slot>
                </div>
              </custom-tooltip>
              <span class="material-icons-round logo successful" :style="`color: ${successfulIcon.color}`" v-else-if='!isVulnerable'>
                {{ successfulIcon.iconName }}
              </span>
              <custom-tooltip popper-class="light-tooltip" v-else>
                <div slot='buttonContent'>
                 <span class="material-icons-round logo" :style="`color: ${successfulIcon.color}`" @click='goToIssues'>
                    {{ successfulIcon.iconName }}
                  </span>
                </div>
                <div slot="content">
                  <slot name="tooltip">
                    {{ warningToolTipWithExtension }}
                  </slot>
                </div>
              </custom-tooltip>
              <unresolved-tag v-if='isHost'/>
              <custom-tooltip class='new-item' v-if='!isHost' placement="bottom" popper-class="light-tooltip">
                <div slot='buttonContent'>
                  <div class="tooltip-badge-container">
                    <v-badge
                      tile
                      overlap
                      :offset-y='25'
                      :offset-x='25'
                      :value='isNew || isManual'
                    >
                      <div class='url-title' :class="{ successful: !isVulnerable }">
                        <v-icon  class='ssl-indicator'
                                 @click.stop="showCertificatePopup = !showCertificatePopup"
                                 :style="{'--ssl-indicator-color': getSSLIndicatorColor}"
                                 size='21'
                        >
                          https
                        </v-icon>
                        {{ title }}
                      </div>
                      <template v-slot:badge>
                        <div class='badges-wrapper'>
                          <new-icon v-if='isNew' class='new-icon'/>
                          <v-icon v-if='isManual' class='manual-config-badge' style='--manual-offset-x:-1px; --manual-offset-y:-9px'>build</v-icon>
                        </div>
                      </template>
                    </v-badge>
                  </div>
                </div>
                <div slot="content">
                  <slot name="tooltip">
                    <div class='url'>
                      {{ title }}
                    </div>
                  </slot>
                </div>
              </custom-tooltip>
              <span class='copy' v-if='!isHost'>
                <custom-tooltip placement="top" popper-class="light-tooltip">
                  <div slot='buttonContent'>
                      <v-btn icon class="copy-btn" target="_blank" @click.stop='copyAssetName' :ripple="false">
                        <v-icon>content_copy</v-icon>
                      </v-btn>
                  </div>
                  <div slot="content">
                    <slot name="tooltip">
                      {{ $t('asset.name.copy.title') }}
                    </slot>
                  </div>
                </custom-tooltip>
              </span>
             <span class='sitemap'>
                <custom-tooltip placement="top" popper-class="light-tooltip">
                  <div slot='buttonContent'>
                      <v-btn v-if='hasSitemap' icon class="sitemap-btn" target="_blank" @click.stop='openSiteMapPopup' :ripple="false">
                        <v-icon class='material-icons-outlined'>map</v-icon>
                      </v-btn>
                  </div>
                  <div slot="content">
                    <slot name="tooltip">
                      {{ $t('asset.name.sitemap.title') }}
                    </slot>
                  </div>
                </custom-tooltip>
              </span>
            </div>
          </div>
          <div class="configuration-item__content__info__waf">
            <div class='header' v-if='displayHeaders' @click.stop='headerClicked("infrastructure.tab.waf.header")'>
              {{ $t('infrastructure.tab.waf.header') }}
              <span v-if='lastHeaderClicked === "infrastructure.tab.waf.header"' class='material-icons-outlined direction'>
                {{ directionIcon }}
              </span>
            </div>
            <div class='content'>
              <div class='waf-icon-indication'>
                <waf-loading-spinner class='spinner' v-if='pendingProtectWafAction || pendingRevertWafAction'/>
                <waf-icon class='icon' :class="behindWafClass" v-else />
              </div>
              <div class='waf-vendor' v-if='!(pendingProtectWafAction || pendingRevertWafAction)'> {{wafVendor}}</div>
            </div>
          </div>
          <div class="configuration-item__content__info__hosts">
            <div class='header' v-if='displayHeaders' @click.stop>
              {{ $t('infrastructure.tab.hosts.and.ports.header') }}
            </div>
            <div class='content'>
              <span v-for="(host, index) in hostsForDisplay" :key="index" class='item-value'>
                <infrastructure-hosts-ports-row
                  :host='host.configData'
                  :ports='host.ports || []'
                  :is-manual='host.isManual'
                  :website='website.subdomain'
                  :new-ports='newPorts'
                  :vulnerable-ports-by-host='vulnerablePortsByHost'
                  :ports-header='$t("infrastructure.tab.ports.header")'
                  :hosts-header='$t("infrastructure.tab.hosts.header")'
                  :geo-indicator='hostGeoIndicator(host)'
                  :behind-noisy-waf='!!host.behindNoisyWaf'
                />
                <v-divider class='hosts-ports-divider' v-if='index !== hostsForDisplay.length - 1'/>
              </span>
              <custom-tooltip class="show-all-hosts-tooltip" placement="right" popper-class="light-tooltip" v-if='displayShowMoreHostsBtn'>
                <div slot='buttonContent'>
                  <span class='show-all-hosts material-icons-outlined' @click.stop='toggleShowAllHosts'>
                    {{ iconNameForShowAllHosts }}
                  </span>
                </div>
                <div slot="content">
                  <slot name="tooltip">
                    {{ tooltipLabelForShowAllHosts }}
                  </slot>
                </div>
              </custom-tooltip>
            </div>
          </div>
          <div class="configuration-item__content__info__services">
            <div class='header' v-if='displayHeaders' @click.stop='headerClicked("infrastructure.tab.services.header")'>
              {{ $t('infrastructure.tab.services.header') }}
              <span v-if='lastHeaderClicked === "infrastructure.tab.services.header"' class='material-icons-outlined direction'>
                {{ directionIcon }}
              </span>
            </div>
            <services :hosts='hosts'/>
          </div>
          <div class='configuration-item__content__info__screenshots'>
            <div class='header' v-if='displayHeaders' @click.stop='headerClicked("infrastructure.tab.screenshots.header")'>
              {{ $t('infrastructure.tab.screenshots.header') }}
              <span v-if='lastHeaderClicked === "infrastructure.tab.screenshots.header"' class='material-icons-outlined direction'>
                {{ directionIcon }}
              </span>
            </div>
            <div class='content'>
              <thumbnail-slider
                v-if='tab==="infrastructure"'
                :items='thumbnails'
                @thumbnailClicked='openScreenshotPopup'
              />
            </div>
          </div>
          <div class="configuration-item__content__info__technologies">
            <div class='header' v-if='displayHeaders' @click.stop='headerClicked("infrastructure.tab.technologies.header")'>
              {{ $t('infrastructure.tab.technologies.header') }}
              <span v-if='lastHeaderClicked === "infrastructure.tab.technologies.header"' class='material-icons-outlined direction'>
                {{ directionIcon }}
              </span>
            </div>
            <div class='configuration-item__content__info__technologies__content'>
              <div class='configuration-item__content__info__technologies__content__list' :style="`flex-wrap: ${isShowAll ? 'wrap' : 'nowrap'};`">
                <div
                  v-for='(tech, index) in collapsedTechnologiesForWebsite'
                  :key='index' class='tech-section' :class='{ vulnerable: vulnerableTechnologies.includes(tech) }'
                  @click.stop='toggleTechnologyDescriptionPopup(tech)'>
                  <div class='tech' :class='{ vulnerable: vulnerableTechnologies.includes(tech) }'>
                    {{ tech }}
                  </div>
                </div>
              </div>
              <div class='configuration-item__content__info__technologies__content__display-toggle' :class='{ open: isShowAll}' v-if='isShowAllTechnologies'>
                <span text class='hide-technologies-btn' @click.stop="isShowAll = !isShowAll">
                  {{ captionOfTechnologiesToggleButton }}
                </span>
              </div>
            </div>
          </div>
          <div class="configuration-item__content__info__last-scanned">
            <div class='header' v-if='displayHeaders' @click.stop='headerClicked("infrastructure.tab.last.scanned.header")'>
              {{ $t('infrastructure.tab.last.scanned.header') }}
              <span v-if='lastHeaderClicked === "infrastructure.tab.last.scanned.header"' class='material-icons-outlined direction'>
                {{ directionIcon }}
              </span>
            </div>
            <div class='content'>
              <div class='last-scanned-date' v-if='lastScannedDate'> {{lastScannedDate}}</div>
            </div>
          </div>
          <div class="configuration-item__content__info__actions">
            <div class="configuration-item__content__info__actions__open" v-if="showOpenLink">
              <custom-tooltip placement="right" popper-class="light-tooltip">
                <div slot='buttonContent'>
                  <v-btn  class="open-btn" :href="formatUrl" target="_blank" :ripple="false">
                    <v-icon>open_in_new</v-icon>
                  </v-btn>
                </div>
                <div slot="content">
                  <slot name="tooltip">
                    open
                  </slot>
                </div>
              </custom-tooltip>
            </div>
          </div>
          <div class='configuration-item__content__info__add-test' v-if='isAssetResolved'>
            <custom-tooltip placement="right" popper-class="light-tooltip" v-if="!isOpen">
              <div slot='buttonContent'>
                <v-btn class="add-test-btn" @click.stop="openAddTestPopup" :ripple="false" :disabled='addTestCardDisabled'>
                  <v-icon class="add-test-icon-no-txt">add</v-icon>
                </v-btn>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ toolTipText }}
                </slot>
              </div>
            </custom-tooltip>
          </div>
          <v-icon v-if='displayOpenedRow' class="expand-icon" :class="{ expand: isOpen }">expand_more</v-icon>
        </div>
        <div class="test-container" v-if="isOpen">
          <test-card
            v-for="(testCard, key) in testCardsForDisplay"
            :test-card="testCard"
            :item="testCard.url"
            :key="key"
            @displayError='displayTestCardError'
          />
          <div v-if='isAssetResolved && !addTestCardDisabled' class="add-test-area" v-ripple="{ class: `blue--text` }" @click="openAddTestPopup">
            <v-icon class="add-test-icon">add</v-icon>
            <span class="add-test-btn-txt">{{ $t('configuration.add.test') }}</span>
          </div>
          <div class='test-card-display-toggle' v-if='allTestCardsForRowAssets.length > this.amountOfTestCardInRow'>
          <span text class='toggle-test-card-btn' @click.stop="displayAllTestCards = !displayAllTestCards">
            {{ displayTestCardsButtonLabel }}
          </span>
          </div>
        </div>
      </div>
      <div class='configuration-item__content__tags'>
        <div class='configuration-item__content__tags__tags-list'>
          <v-chip
            label
            outlined
            :ripple='false'
            close
            pill
            text-color='#2459d5'
            small
            class='tag'
            v-for='(tagKey, index) in nonExcludedTagKeys'
            :key='index'
            close-icon='close'
            @click:close='removeTagFromList(tagKey)'
            @click.stop='addTagFilter(tagKey)'
          >
            {{ tagKey }}
          </v-chip>
          <v-chip
            label
            outlined
            close
            pill
            text-color='#2baf69'
            small
            class='auto-tag'
            v-for='(tagKey, index) in nonExcludedAutoTagKeys'
            :key='index + 100'
            close-icon='close'
            @click:close='deleteAutoTagClicked(tagKey)'
            @click.stop='addTagFilter(tagKey)'
          >
            {{ tagKey }}
          </v-chip>
        </div>
        <span class='no-tags' v-if='nonExcludedTagKeys.length === 0 && nonExcludedAutoTagKeys.length === 0'>
          {{ $t('no.tags') }}
        </span>
        <span class="material-icons" @click='openAddTagPopup'>
          local_offer
        </span>
      </div>
    </div>
    <success-operation-message
      :message='successMessage'
      @close="clearSuccessMessage"
    />
    <certificate-popup
      title='host.certificate.popup.title'
      :visible-popup='showCertificatePopup'
      :showNoButton='false'
      yes-caption='no.selected.employees.popup.ok'
      :data='certificateInfo'
      @clicked='showCertificatePopup = false'
      v-if="certificateInfo"
    />
  </div>
</template>

<script>
import moment from 'moment';
import indexDB from '@/assets/tech-metadata/index-db.json';
import { mapGetters } from 'vuex';
import { CONFIGURATION, CONFIGURATION_ACTION_TYPES } from '@/constants/configuration';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';
import TestCard from '@/components/configuration/TestCard.vue';
import TestStatusFailureIcon from '@/assets/icons/warning_icn@1x.svg?inline';
import NewIcon from '@/assets/icons/new-icon.svg?inline';
import { TEST_TYPES_HOSTS, TEST_TYPES_SUBDOMAINS } from '@/constants/projects';
import ThumbnailSlider from '@/components/common/ThumbnailSlider.vue';
import InfrastructureHostsPortsRow from '@/components/configuration/InfrastructureHostsPortsRow.vue';
import wafIcon from '@/assets/icons/waf.svg?inline';
import _ from 'lodash';
import { views } from '@/router/constants';
import UnresolvedTag from '@/components/common/UnresolvedTag.vue';
import WafLoadingSpinner from '@/components/common/WafLoadingSpinner.vue';
import Services from '@/components/configuration/Services.vue';
import SuccessOperationMessage from '@/components/common/SuccessOperationMessage.vue';
import { RESET_SUCCESS_OPERATION_MESSAGE } from '@/store/configuration/actionTypes';
import { CALCULATE_LAST_SCAN_DATA } from '@/store/project/actionTypes';
import CertificatePopup from '@/components/popup/CertificatePopup.vue';
import CustomTooltip from '../common/CustomTooltip.vue';

export default {
  name: 'InfrastructureItemRow',
  props: {
    addTestCardDisabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    amountOfTestCardInRow: {
      type: Number,
      default: 5,
      required: false,
    },
    currentSelectedTld: {
      type: String,
      default: null,
    },
    isManual: {
      type: Boolean,
      default: () => false,
    },
    isSelected: {
      type: Boolean,
      default: () => false,
    },
    showOpenLink: {
      type: Boolean,
      default: false,
    },
    organization: {
      type: String,
      default: '',
      required: false,
    },
    website: {},
    testTypes: {},
    tab: {},
    displayHeaders: {
      type: Boolean,
      default: false,
    },
    sortDirection: {
      type: String,
      required: false,
      default: 'keyboard_arrow_up',
    },
    sortOption: {
      type: String,
      required: false,
      default: '',
    },
    clickedHeaderValue: {
      required: false,
      default: () => {},
      type: Object,
    },
  },
  components: {
    CertificatePopup,
    Services,
    WafLoadingSpinner,
    UnresolvedTag,
    wafIcon,
    ThumbnailSlider,
    CustomTooltip,
    CustomCheckbox,
    TestCard,
    TestStatusFailureIcon,
    NewIcon,
    InfrastructureHostsPortsRow,
    SuccessOperationMessage,
  },
  data() {
    return {
      isOpen: false,
      isShowAll: false,
      displayAllTestCards: false,
      autoHijackInProgress: false,
      showAllHosts: false,
      warningToolTipExtension: '',
      polling: null,
      pollingHijack: null,
      wafMetaData: [],
      successMessage: '',
      windowInnerWidth: window.innerWidth,
      itemClicksCounter: {},
      lastHeaderClicked: this.sortOption || '',
      directionIcon: this.sortDirection || 'keyboard_arrow_up',
      showCertificatePopup: false,
    };
  },
  watch: {
    'website.wafIntegrationData': {
      handler() {
        this.wafMetaData = this.website?.wafIntegrationData || [];
      },
      deep: true,
    },
    'website.hijack': {
      handler(newVal, oldVal) {
        if (this.hijackedSubdomain) {
          const oldErrorCode = oldVal.length ? (JSON.parse(`${oldVal}`).errorCode || 0) : 0;
          const newErrorCode = newVal.length ? (JSON.parse(`${newVal}`).errorCode || 0) : 0;
          if (oldErrorCode < newErrorCode) {
            this.warningToolTipExtension = this.$t('configuration.infrastructure.assets.auto.hijack.failed.tooltip');
          } else {
            this.warningToolTipExtension = '';
          }
          this.autoHijackInProgress = false;
        }
      },
      deep: true,
    },
    async pendingProtectWafAction(newValue) {
      if (newValue) {
        await this.pollData();
      }
    },
    async pendingRevertWafAction(newValue) {
      if (newValue) {
        await this.pollData();
      }
    },
    async autoHijackInProgress(newValue) {
      if (newValue) {
        await this.pollDataForHijack();
      }
    },
    successOperationMessage() {
      this.successMessage = this.successOperationMessage;
    },
  },
  async beforeDestroy() {
    clearInterval(this.polling);
    clearInterval(this.pollingHijack);
  },
  async created() {
    if (this.hasWafConfiguration) {
      this.wafMetaData = this.website.wafIntegrationData;
    }
    if (this.pendingProtectWafAction || this.pendingRevertWafAction) {
      await this.pollData();
    }
    window.addEventListener('resize', this.windowResizeHandler);
  },
  destroyed() {
    window.removeEventListener('resize', this.windowResizeHandler);
  },
  methods: {
    clearInterval() {
      clearInterval(this.polling);
    },
    clearIntervalHijack() {
      clearInterval(this.pollingHijack);
    },
    windowResizeHandler() {
      this.windowInnerWidth = window.innerWidth;
    },
    async pollData() {
      this.polling = setInterval(async () => {
        if (this.pendingProtectWafAction || this.pendingRevertWafAction) {
          // INTEGRATION IN PROGRESS
          await this.$store.dispatch(CALCULATE_LAST_SCAN_DATA, { domain: [this.website.tld], timeRange: null });
          await this.$emit('loadInfrastructure', false);
        } else {
          // INTEGRATION FINISHED
          this.clearInterval();
        }
      }, 60000);
    },
    async pollDataForHijack() {
      this.pollingHijack = setInterval(async () => {
        if (this.autoHijackInProgress) {
          // INTEGRATION IN PROGRESS
          await this.$store.dispatch(CALCULATE_LAST_SCAN_DATA, { domain: [this.website.tld], timeRange: null });
          await this.$emit('loadInfrastructure', false);
        } else {
          // INTEGRATION FINISHED
          this.clearIntervalHijack();
        }
      }, 60000);
    },
    isHostIpv6(host) {
      return host.includes(':');
    },
    toggleShowAllHosts() {
      this.showAllHosts = !this.showAllHosts;
    },
    async goToIssues() {
      await this.$router.push({ name: views.ISSUES, params: { tld: this.issuesTextSearch, specificSearch: (!this.isHost).toString() }});
    },
    openAddTagPopup() {
      this.$emit('openTagsPopup', [this.website.subdomain]);
    },
    addTagFilter(tag) {
      this.$emit('addTagFilter', tag);
    },
    async deleteAutoTagClicked(tag) {
      const payload = {
        tag,
        currentAutoTags: {...this.currentAutoTags},
        currentAutoTagKeys: [...this.currentAutoTagKeys],
        title: this.title,
        tld: this.website.tld,
      };
      this.$emit('openDeleteAutoTagPopup', payload);
    },
    async removeTagFromList(tag) {
      const tagsUpdated = {...this.currentSubdomainTags};

      if (this.currentDomainsTagKeys?.includes(tag) || this.currentOrganizationsTagKeys?.includes(tag)) {
        tagsUpdated[tag] = 'exclude';
      } else if (this.currentSubdomainTagKeys.includes(tag)) {
        delete tagsUpdated[tag];
      }

      await this.$emit('submitTagsInformation', {
        action: CONFIGURATION_ACTION_TYPES.REMOVE_TAG,
        configType: CONFIGURATION.CONFIG_TYPES.TAGS_SUBDOMAIN,
        items: [{
          url: this.title,
          tld: this.website.tld,
          tags: { ...tagsUpdated },
        }],
      });
    },
    async openAddTestPopup() {
      if (this.isAssetResolved) {
        await this.$emit('openAddTestPopup', {
          websiteTitle: this.title,
          testTypesFiltered: this.testTypesFiltered,
          hosts: this.hostsKeys,
          ports: this.ports,
          tld: this.website.tld,
        });
      }
    },
    async openSiteMapPopup() {
      await this.$emit('openSitemapPopup', { sitemap: this.sitemap, website: this.title, features: this.features });
    },
    async openScreenshotPopup(index) {
      await this.$emit('openScreenshotPopup', { screenshots: [...this.screenshotsForWebsite], focusedIndex: index });
    },
    async displayTestCardError(errorMessage) {
      await this.$emit('displayTestCardError', errorMessage);
    },
    sortTechnologies(a, b) {
      const aVulnerable = this.vulnerableTechnologies.includes(a);
      const bVulnerable = this.vulnerableTechnologies.includes(b);
      if ((aVulnerable && bVulnerable) || (!aVulnerable && !bVulnerable)) {
        return a > b ? 1 : -1;
      }
      return aVulnerable ? -1 : 1;
    },
    async toggleTechnologyDescriptionPopup(tech) {
      await this.$emit('toggleTechnologyDescriptionPopup', tech);
    },
    async initiateActionOnEnableSubdomain() {
      await this.$emit('enableSubdomain', {
        configType: CONFIGURATION.CONFIG_TYPES.SUBDOMAIN, data: this.website.subdomain, tld: this.website.tld, metaData: this.website.metaData || [],
      });
    },
    async initiateTakeOverOnHijackedAsset() {
      this.autoHijackInProgress = true;
      await this.$emit('commitHijackActionAsset', {
        tld: this.website.tld, target: this.website.subdomain, action: CONFIGURATION.AUTO_HIJACK_ACTIONS.takeover, successMessageType: CONFIGURATION_ACTION_TYPES.TAKEOVER,
      });
    },
    async initiateReleaseOnHijackedAsset() {
      this.autoHijackInProgress = true;
      await this.$emit('commitHijackActionAsset', {
        tld: this.website.tld, target: this.website.subdomain, action: CONFIGURATION.AUTO_HIJACK_ACTIONS.release, successMessageType: CONFIGURATION_ACTION_TYPES.RELEASE,
      });
    },
    async initiateProtectWafOnAsset() {
      await this.$emit('openWafPopup', {
        tld: this.website.tld, target: this.website.subdomain, action: CONFIGURATION.WAF_ACTIONS.protect,
      });
    },
    async initiateReleaseWafOnAsset() {
      await this.$emit('openWafPopup', {
        tld: this.website.tld, target: this.website.subdomain, action: CONFIGURATION.WAF_ACTIONS.revert, integration: this.website.wafIntegrationData[0],
      });
    },
    hostGeoIndicator(host) {
      if (host?.providerData && host.providerData?.geolocation?.country_code) {
        return host.providerData?.geolocation;
      }
      return null;
    },
    checkIfActionDisabled(id) {
      if (id === CONFIGURATION.INFRASTRUCTURE_ACTIONS_TYPES_SUBDOMAIN.moveSubdomain) {
        if (this.isHost) {
          return true;
        }
        return !this.isManual;
      }
      if (id === CONFIGURATION.INFRASTRUCTURE_ACTIONS_TYPES_SUBDOMAIN.moveIp) {
        if (!this.manualHostsKeys.length) {
          return true;
        }
        return false;
      }
      if (CONFIGURATION.INFRASTRUCTURE_ACTIONS_TYPES.suppress.includes(id)) {
        if (this.isHost && id !== CONFIGURATION.INFRASTRUCTURE_SUPPRESS_TYPES.ip) {
          return true;
        }
        return false;
      }
      return false;
    },
    copyAssetName() {
      const elem = document.createElement('textarea');
      elem.value = this.title;
      document.body.appendChild(elem);
      elem.select();
      document.execCommand('copy');
      document.body.removeChild(elem);
    },
    async initiateActionFromMenu(id) {
      if (id === CONFIGURATION.INFRASTRUCTURE_ACTIONS_TYPES_SUBDOMAIN.moveSubdomain) {
        this.$emit('openMoveSubdomainPopup', { website: this.website.subdomain, currentSelectedTld: this.currentSelectedTld });
      } else if (id === CONFIGURATION.INFRASTRUCTURE_ACTIONS_TYPES_SUBDOMAIN.moveIp) {
        this.$emit('openMoveIpPopup', { website: this.website, currentSelectedTld: this.currentSelectedTld });
      } else if (id === CONFIGURATION.INFRASTRUCTURE_SUPPRESS_TYPES.ip) {
        this.$emit('openSuppressPortsPopup', { hosts: this.hosts });
      } else if (id === CONFIGURATION.INFRASTRUCTURE_SUPPRESS_TYPES.enable) {
        await this.initiateActionOnEnableSubdomain();
      } else if (id === CONFIGURATION.INFRASTRUCTURE_SUPPRESS_TYPES.subdomain) {
        this.$emit('openSuppressSubdomainPopup', { website: this.title, tld: this.website.tld, metaData: this.website.metaData || [] });
      } else if (id === CONFIGURATION.INFRASTRUCTURE_HIJACK_ACTIONS_TYPES.takeOver) {
        await this.initiateTakeOverOnHijackedAsset();
      } else if (id === CONFIGURATION.INFRASTRUCTURE_HIJACK_ACTIONS_TYPES.release) {
        await this.initiateReleaseOnHijackedAsset();
      } else if (id === CONFIGURATION.INFRASTRUCTURE_WAF_ACTIONS_TYPES.protect) {
        await this.initiateProtectWafOnAsset();
      } else if (id === CONFIGURATION.INFRASTRUCTURE_WAF_ACTIONS_TYPES.revert) {
        await this.initiateReleaseWafOnAsset();
      } else if (id === CONFIGURATION.INFRASTRUCTURE_REMOVE_ACTIONS_TYPES.removeAsset) {
        this.$emit('openRemoveAssetPopup', { website: this.website.subdomain, tld: this.website.tld, metaData: this.website.metaData || [] });
      }
    },
    toggleOpenRow() {
      if (this.displayOpenedRow) {
        this.isOpen = !this.isOpen;
      } else {
        this.isOpen = false;
      }
    },
    async clearSuccessMessage() {
      await this.$store.dispatch(RESET_SUCCESS_OPERATION_MESSAGE);
    },
    getPortFromUrl(url) {
      const port = url?.split('_')?.slice(-1)[0]?.split('.')[0] || '';
      if (port && port.length > 0 && this.isNum(port)) {
        return port;
      }
      const assetPart = url?.split('/')?.slice(-1)[0] || '';
      if (assetPart.startsWith('https')) {
        return '443';
      }
      if (assetPart.startsWith('http')) {
        return '80';
      }
      return '';
    },
    isNum(value) {
      return /^\d+$/.test(value);
    },
    headerClicked(header) {
      this.lastHeaderClicked = header;
      if (!Object.keys(this.itemClicksCounter).includes(header)) {
        this.itemClicksCounter[header] = 0;
      }
      this.itemClicksCounter[header] += 1;
      if (this.clickedHeaderValue) {
        if (Object.keys(this.clickedHeaderValue).includes(header)) {
          if (this.clickedHeaderValue[header] === 2) {
            this.itemClicksCounter[header] = 0;
          }
          this.itemClicksCounter[header] += 1;
        }
      }
      this.directionIcon = this.itemClicksCounter[header] % 2 === 0 ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
      this.$emit('headerClicked', { header, clicks: this.itemClicksCounter[header] });
    },
    async openSensitivityPopup() {
      await this.$emit('openSensitivityPopup', this.website.subdomain, this.website.tld, this.sensitivity);
    },
  },
  computed: {
    ...mapGetters([
      'testCardsSubdomains',
      'testCardsHosts',
    ]),
    getSSLIndicatorColor() {
      if (this.website?.certificate && Object.keys(this.website.certificate).length > 0) {
        const daysLeft = this.website.certificate.DaysLeft;
        // eslint-disable-next-line no-nested-ternary
        return daysLeft <= 0 ? '#e0255e' : daysLeft <= 31 ? '#ff7f00' : '#2BAF69';
      }
      return '#9fa5b4';
    },
    certificateInfo() {
      if (this.website?.certificate && Object.keys(this.website.certificate).length > 0) {
        return this.website.certificate;
      }
      return false;
    },
    issuesTextSearch() {
      return this.title || this.website.hosts?.[0]?.configData || '';
    },
    toolTipText() {
      return this.addTestCardDisabled ? this.$t('license.disable.above.3.scans') : this.$t('configuration.add.test');
    },
    isShowAllTechnologies() {
      return this.technologiesForWebsite.length > (this.windowInnerWidth - 550) / 550;
    },
    warningTooltip() {
      if (this.successfulIcon.severity === 'critical') {
        return this.$t('configuration.critical.vulnerable.asset.tooltip');
      }
      if (this.successfulIcon.severity === 'medium') {
        return this.$t('configuration.medium.vulnerable.asset.tooltip');
      }
      return this.$t('configuration.low.vulnerable.asset.tooltip');
    },
    sensitivityTooltip() {
      if (this.sensitivity === 'high') {
        return this.$t('configuration.high.sensitivity.asset.tooltip');
      }
      if (this.sensitivity === 'medium') {
        return this.$t('configuration.medium.sensitivity.asset.tooltip');
      }
      if (this.sensitivity === 'low') {
        return this.$t('configuration.low.sensitivity.asset.tooltip');
      }
      if (this.sensitivity === 'informative') {
        return this.$t('configuration.informative.sensitivity.asset.tooltip');
      }
      return this.$t('configuration.critical.sensitivity.asset.tooltip');
    },
    hijackableTooltip() {
      return this.$t('configuration.infrastructure.assets.tooltip');
    },
    noPocTooltip() {
      return this.$t('configuration.infrastructure.assets.noPoc.tooltip');
    },
    wafVendor() {
      let modifiedString;
      if (this.wafMetaData.length > 0) {
        if (this.wafMetaData[1] === 'protect') {
          [modifiedString] = this.wafMetaData;
        } else if (this.wafMetaData[1] === 'revert') {
          modifiedString = '';
        }
      } else {
        modifiedString = this.website.wafVendor;
      }
      if (modifiedString?.includes('Lite')) {
        modifiedString = modifiedString?.replace('Lite', '').trim();
      }
      return modifiedString;
    },
    getTooltipForAsset() {
      if (this.website.noPoc) {
        return this.noPocTooltip;
      }
      if (this.website.hijack) {
        return this.hijackableTooltip;
      }
      if (this.website.vulnerable || this.isVulnerable) {
        return this.warningTooltip;
      }
      return this.warningTooltip;
    },
    hostsSuccessfulDetails() {
      const hosts = this.website?.hosts || [];
      const successfulDetails = {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
      };
      hosts.forEach(host => {
        const currentSuccessfulDetails = host?.successfulDetails || {};
        Object.keys(currentSuccessfulDetails).forEach(key => {
          successfulDetails[key] += currentSuccessfulDetails[key];
        });
      });
      return successfulDetails;
    },
    mergedSuccessfulDetails() {
      const {successfulDetails} = this.website;
      Object.keys(successfulDetails).forEach(key => {
        successfulDetails[key] += this.hostsSuccessfulDetails[key];
      });
      return successfulDetails;
    },
    successfulIcon() {
      const successfulDetails = this.mergedSuccessfulDetails;
      const successfulKeys = Object.keys(successfulDetails);
      if ((successfulKeys.includes('critical') && successfulDetails.critical > 0) || (successfulKeys.includes('high') && successfulDetails.high > 0)) {
        return { iconName: 'warning', color: '#E0255E', severity: 'critical' };
      }
      if (successfulKeys.includes('medium') && successfulDetails.medium > 0) {
        return { iconName: 'warning', color: '#e0ad42', severity: 'medium' };
      }
      if (successfulKeys.includes('low') && successfulDetails.low > 0) {
        return { iconName: 'warning', color: '#2459D5', severity: 'low' };
      }
      return { iconName: 'check_circle', color: '#2baf69' };
    },
    tagKeys() {
      return Object.keys(this.tags);
    },
    autoTagKeys() {
      return Object.keys(this.autoTags);
    },
    screenshotsFormatted() {
      return this.website?.screenshots || [];
    },
    screenshotsForWebsite() {
      const screenshots = [];
      this.screenshotsFormatted.forEach(screenshot => {
        const portOfScreenshot = this.getPortFromUrl(screenshot);
        screenshots.push({
          screenshot,
          asset: this.isHost ? screenshot.split('/').slice(-1)[0]
            .split('_')
            .slice(1, -1)
            .join('_')
            .replace(/_/g, '.')
            .split(`.${this.website.tld}`)[0]
            .split('.virtual-subdomain-placeholder.')[0]
            : screenshot.split('/').slice(-1)[0]
              .split('_')
              .slice(1, -1)
              .join('_')
              .replace(/_/g, '.')
              .split('.virtual-subdomain-placeholder.')[0],
          port: portOfScreenshot,
        });
      });
      return screenshots;
    },
    thumbnails() {
      return [...this.screenshotsForWebsite].map(screenshotItem => ({
        website: this.title,
        screenshot: screenshotItem.screenshot,
        port: screenshotItem.port,
        url: `${screenshotItem.screenshot.split('/').slice(0, -1).join('/')}/thumbnail_${screenshotItem.screenshot.split('/').slice(-1)}`,
      }));
    },
    behindWafClass() {
      let behindWafClass = '';
      behindWafClass += !this.behindWaf ? 'not-waf-icon' : 'waf-icon';
      behindWafClass += this.hasLiteWaf ? ' lite' : '';
      return behindWafClass;
    },
    vulnerablePortsByHost() {
      const result = {};
      if (this.website.hosts) {
        Object.keys(this.website.hosts).forEach(hostKey => {
          result[hostKey] = this.website.hosts[hostKey].vulnerablePorts || [];
        });
      }
      return result;
    },
    currentSubdomainTags() {
      return this.website?.subdomainsTags || {};
    },
    currentSubdomainTagKeys() {
      return Object.keys(this.currentSubdomainTags);
    },
    currentDomainTags() {
      return this.website?.domainsTags || {};
    },
    currentOrganizationsTags() {
      return this.website?.organizationsTags || {};
    },
    currentAutoTags() {
      return this.website?.autoTags || {};
    },
    currentDomainsTagKeys() {
      return Object.keys(this.currentDomainTags);
    },
    currentOrganizationsTagKeys() {
      return Object.keys(this.currentOrganizationsTags);
    },
    currentAutoTagKeys() {
      return Object.keys(this.currentAutoTags);
    },
    tags() {
      return {
        ...this.currentOrganizationsTags,
        ...this.currentDomainTags,
        ...this.currentSubdomainTags,
      };
    },
    nonExcludedTagKeys() {
      return this.tagKeys.filter(key => this.tags[key] !== 'exclude');
    },
    autoTags() {
      return { ...this.currentAutoTags };
    },
    nonExcludedAutoTagKeys() {
      return this.autoTagKeys.filter(key => this.autoTags[key] !== 'exclude');
    },
    isNew() {
      return this.website.isNew || false;
    },
    warningToolTipWithExtension() {
      return `${this.getTooltipForAsset} ${this.warningToolTipExtension}`;
    },
    displayTestCardsButtonLabel() {
      return this.displayAllTestCards ? this.$t('hide.test.cards') : this.$t('show.all.test.cards');
    },
    progressColor() {
      return '#2459D5';
    },
    captionOfTechnologiesToggleButton() {
      return this.isShowAll ? this.$t('infrastructure.technologies.hide.list') : this.$t('infrastructure.technologies.show.all.list');
    },
    technologiesForWebsite() {
      return this.getTechnologiesForAsset.technologies;
    },
    hasLiteWaf() {
      return this.wafMetaData.length > 0 ? this.wafMetaData[0]?.includes('Lite') : this.website.wafVendor?.includes('Lite');
    },
    collapsedTechnologiesForWebsite() {
      return this.isShowAll
        ? [...this.technologiesForWebsite].sort((a, b) => this.sortTechnologies(a, b))
        : [...this.technologiesForWebsite].sort((a, b) => this.sortTechnologies(a, b)).slice(0, (this.windowInnerWidth - 550) / 550);
    },
    vulnerableTechnologies() {
      return this.getTechnologiesForAsset.vulnerableTechnologies;
    },
    getTechnologiesForAsset() {
      const result = { technologies: [], vulnerableTechnologies: [] };
      if (this.website) {
        if (this.website.technologies) {
          const technologiesNames = Object.keys(this.website.technologies);
          for (let i = 0; i < technologiesNames.length; i += 1) {
            const currentTechnologyName = technologiesNames[i];
            const currentFullTech = this.website.technologies[currentTechnologyName];
            const formattedTechName = currentTechnologyName.split('/')[1] === '' ? currentTechnologyName.split('/')[0] : currentTechnologyName;
            if (formattedTechName && (Object.keys(indexDB)
              .includes(formattedTechName.split('/')[0].replace(' ', '-').toLowerCase()) ||
              this.website.technologies[currentTechnologyName].tool === 'wappalyzer')) {
              result.technologies.push(formattedTechName);
              if (currentFullTech.vulnerable) {
                result.vulnerableTechnologies.push(formattedTechName);
              }
            }
          }
        }
      }
      return result;
    },
    testTypesFiltered() {
      if (this.isHost) {
        return Object.values(TEST_TYPES_HOSTS);
      } if (!this.hosts.length) {
        return Object.values(TEST_TYPES_SUBDOMAINS);
      }
      return this.testTypes;
    },
    allTestCardsForRowAssets() {
      const testCardsCollector = [];
      const testCardsKeys = Object.keys(this.testCards);
      for (let i = 0; i < this.allAssetsInRow.length; i += 1) {
        const currentAsset = this.allAssetsInRow[i];
        if (testCardsKeys.includes(currentAsset)) {
          this.testCards[currentAsset].forEach(testCard => {
            if (testCard.tld === this.website.tld) {
              testCardsCollector.push(testCard);
            }
          });
        }
      }
      testCardsCollector.sort((a, b) => (moment(a.createdDate).isAfter(moment(b.createdDate)) ? -1 : 1));
      return testCardsCollector;
    },
    testCardsForDisplay() {
      const maxPossibleTestCards = this.allTestCardsForRowAssets.length < this.amountOfTestCardInRow ? this.allTestCardsForRowAssets.length : this.amountOfTestCardInRow;
      return this.displayAllTestCards ? this.allTestCardsForRowAssets : this.allTestCardsForRowAssets.slice(0, maxPossibleTestCards);
    },
    isSuppressed() {
      return this.website?.metaData?.length > 0 && this.website.metaData.includes(this.website.subdomain);
    },
    isHost() {
      return this.website.subdomain === `${this.website.hosts?.[0]?.configData}.${this.website.tld}`;
    },
    isManuallyAddedAsset() {
      // Only assets that were added manually and never appeared in a scan can be removed.
      return !!this.website?.isManuallyAdded && !this.isHost;
    },
    actionsMenuOptions() {
      const actions = Object.keys(CONFIGURATION.INFRASTRUCTURE_ACTIONS_TYPES_SUBDOMAIN).map(item => ({ value: item, title: this.$t(`infrastructure.assets.action.${item}`) }));
      if (this.hasWafConfiguration && !this.pendingRevertWafAction) {
        if ((this.website.wafIntegrationData[2] && this.website.wafIntegrationData[2] === 'finished') || (this.wafMetaData[2] && this.wafMetaData[2] === 'finished')) {
          // eslint-disable-next-line max-len
          if ((this.website.wafIntegrationData[1] && this.website.wafIntegrationData[1] === CONFIGURATION.INFRASTRUCTURE_WAF_ACTIONS_TYPES.protect) || (this.wafMetaData[1] && this.wafMetaData[1] === CONFIGURATION.INFRASTRUCTURE_WAF_ACTIONS_TYPES.protect)) {
            actions.push(
              {
                value: CONFIGURATION.INFRASTRUCTURE_WAF_ACTIONS_TYPES.revert,
                title: this.$t(`infrastructure.assets.action.${CONFIGURATION.INFRASTRUCTURE_WAF_ACTIONS_TYPES.revert}`),
              },
            );
          }
        }
      }
      if (!this.behindWaf && !this.pendingProtectWafAction) {
        actions.push(
          {
            value: CONFIGURATION.INFRASTRUCTURE_WAF_ACTIONS_TYPES.protect,
            title: this.$t(`infrastructure.assets.action.${CONFIGURATION.INFRASTRUCTURE_WAF_ACTIONS_TYPES.protect}`),
          },
        );
      }
      if (this.hijackedSubdomain) {
        if (this.hasBeenTookOver) {
          actions.push(
            {
              value: CONFIGURATION.INFRASTRUCTURE_HIJACK_ACTIONS_TYPES.release,
              title: this.$t(`infrastructure.assets.action.${CONFIGURATION.INFRASTRUCTURE_HIJACK_ACTIONS_TYPES.release}`),
            },
          );
        } else {
          actions.push(
            {
              value: CONFIGURATION.INFRASTRUCTURE_HIJACK_ACTIONS_TYPES.takeOver,
              title: this.$t(`infrastructure.assets.action.${CONFIGURATION.INFRASTRUCTURE_HIJACK_ACTIONS_TYPES.takeOver}`),
            },
          );
        }
      }
      if (this.isManuallyAddedAsset) {
        actions.push(
          {
            value: CONFIGURATION.INFRASTRUCTURE_REMOVE_ACTIONS_TYPES.removeAsset,
            title: this.$t(`infrastructure.assets.action.${CONFIGURATION.INFRASTRUCTURE_REMOVE_ACTIONS_TYPES.removeAsset}`),
          },
        );
      }
      return actions;
    },
    hasBeenTookOver() {
      return this.website?.hijackIntegrationData?.metaData || false;
    },
    hasWafConfiguration() {
      return this.website?.wafIntegrationData;
    },
    pendingProtectWafAction() {
      return this.wafMetaData.length > 0 && this.wafMetaData?.[1] === 'protect' && this.wafMetaData?.[2] !== 'finished';
    },
    pendingRevertWafAction() {
      return this.wafMetaData.length > 0 && this.wafMetaData?.[1] === 'revert' && this.wafMetaData?.[2] !== 'finished';
    },
    configurationSubActionTypesMap() {
      const configurationActionTypes = CONFIGURATION.INFRASTRUCTURE_ACTIONS_TYPES;
      const configurationSubActionsForSuppress = ['ip'];
      if (this.isSuppressed) {
        configurationSubActionsForSuppress.push('enable');
      } else {
        configurationSubActionsForSuppress.push('subdomain');
      }
      configurationActionTypes.suppress = configurationSubActionsForSuppress;
      return configurationActionTypes;
    },
    additionalActionOptions() {
      return Object.keys(CONFIGURATION.INFRASTRUCTURE_ACTIONS_TYPES)
        .map(item => ({
          value: item,
          title: this.$t(`infrastructure.assets.action.${item}`),
          subActions: this.configurationSubActionTypesMap[item]
            .map(subItem => ({ value: subItem, title: this.$t(`infrastructure.assets.sub.action.${item}.${subItem}`)})),
        }));
    },
    testCardsInfrastructure() {
      return { ...this.testCardsHosts, ...this.testCardsSubdomains };
    },
    testCards() {
      return this.testCardsInfrastructure || [];
    },
    title() {
      return this.isHost ? '' : this.website?.subdomain;
    },
    sitemap() {
      return this.isHost ? {} : this.website?.sitemap;
    },
    features() {
      return this.isHost ? {} : this.website?.features;
    },
    hasSitemap() {
      return !_.isEmpty(this.sitemap);
    },
    isVulnerable() {
      return Object.keys(this.mergedSuccessfulDetails).some(key => this.mergedSuccessfulDetails[key] > 0);
    },
    formatUrl() {
      const url = this.title;
      return `http://${url}`;
    },
    hostsSortedByType() {
      return [...this.hosts].sort((a, b) => {
        if (a?.ports?.length > b?.ports?.length) {
          return -1;
        }
        if (b?.ports?.length > a?.ports?.length) {
          return 1;
        }
        const aIpv6 = this.isHostIpv6(a.configData);
        const bIpv6 = this.isHostIpv6(b.configData);
        if (aIpv6 && !bIpv6) {
          return 1;
        }
        if (!aIpv6 && bIpv6) {
          return -1;
        }
        return -1;
      });
    },
    hosts() {
      return this.website?.hosts || [];
    },
    hostsForDisplay() {
      return this.showAllHosts ? this.hostsSortedByType : this.hostsSortedByType.slice(0, 3);
    },
    newPorts() {
      return this.website?.newPorts?.ports?.length > 0 ? this.website.newPorts.ports : [];
    },
    displayShowMoreHostsBtn() {
      return this.hosts.length > 3;
    },
    iconNameForShowAllHosts() {
      return this.showAllHosts ? 'unfold_less' : 'unfold_more';
    },
    tooltipLabelForShowAllHosts() {
      return this.showAllHosts ? this.$t('hide.hosts.label') : this.$t('show.all.hosts.label');
    },
    isAssetResolved() {
      return this.hosts.length;
    },
    displayOpenedRow() {
      return this.isAssetResolved || this.testCardsForDisplay.length;
    },
    ports() {
      let portsCollector = [];
      this.hosts.forEach(host => {
        portsCollector = portsCollector.concat(host.ports);
      });
      return Array.from(new Set(portsCollector));
    },
    hostsKeys() {
      return this.hosts.map(host => host.configData);
    },
    allAssetsInRow() {
      return this.isHost ? this.hostsKeys : this.hostsKeys.concat(this.website.subdomain);
    },
    manualHostsKeys() {
      return this.hosts.filter(host => host.isManual).map(item => item.configData);
    },
    hijackedSubdomain() {
      return this.website?.hijack || false;
    },
    behindWaf() {
      if (this.wafMetaData.length > 0 && this.wafMetaData[1] === 'protect' && this.wafMetaData[2] === 'finished') {
        return true;
      }
      if (this.wafMetaData.length > 0 && this.wafMetaData[1] === 'revert' && this.wafMetaData[2] === 'finished') {
        return false;
      }
      return this.website.behindWaf || false;
    },
    noPocSubdomain() {
      return this.website.noPoc;
    },
    lastScannedDate() {
      if (this.website?.lastScannedDate && this.website?.lastScannedDate !== '') {
        return moment(this.website.lastScannedDate).format('DD MMM YYYY');
      }
      return '';
    },
    sensitivity() {
      return this.website?.sensitivity || 'critical';
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/design-system';

.theme--light.v-list {
  width: rem(250);
}

.v-list-item {
  min-height: rem(30);
  padding-right: rem(30);
  padding-left: rem(30);
}

.list-group-title {
  padding-left: $space-4;
}

.list-sub-group-item {
  padding-left: $space-4;
}

.v-list-item__title {
  font-family: $default-application-font-medium;
  font-size: $font-size-sm;
}

#app .configuration-item {
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  padding: $space-3 0;

  &__control {
    margin-right: 5px;
    margin-top: 8px;
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 15px;
    &__info {
      width: 100%;
      &__actions {
        flex-direction: row;
        display: flex;
        width: 2%;
        justify-content: flex-end;
        &__open{
          padding-top: 2px;
        }
      }
      &__add-test {
        position: absolute;
        right: 3.5%;
      }
      .header {
        margin-top: 24px;
      }
      &__services {
        min-width: 90px;
        max-width: 90px;
        margin-left: 35px;
        .header {
          display: flex;
          align-items: center;
          position: absolute;
          top: 100px;
          margin-left: 2px;
          font-family: $default-application-font-regular;
          font-size: 12px;
          letter-spacing: 1.1px;
          line-height: 15px;
          color: $widget-legend-color;
          text-transform: uppercase;
          height: 30px;
          &:hover {
            cursor: pointer;
          }
        }
      }
      &__screenshots {
        display: flex;
        flex-direction: column;
        margin-left: 36px;
        min-width: 23%;
        max-width: 23%;
        .header {
          display: flex;
          align-items: center;
          position: absolute;
          top: 100px;
          margin-left: 15px;
          font-family: $default-application-font-regular;
          font-size: 12px;
          letter-spacing: 1.1px;
          line-height: 15px;
          color: $widget-legend-color;
          text-transform: uppercase;
          height: 30px;
          &:hover {
            cursor: pointer;
          }
        }
        .content {
          margin-left: 15px;
          width: 462px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          &__screenshot {
            margin-right: 8px;
            transition: transform 0.2s ease-in-out;
            &:hover {
              cursor: pointer;
            }
          }
        }
      }
      &__technologies {
        display: flex;
        flex-direction: column;
        margin-top: 20px;
        width: 14%;
        margin-left: -80px;
        .header {
          display: flex;
          align-items: center;
          position: absolute;
          top: 100px;
          font-family: $default-application-font-regular;
          font-size: 12px;
          letter-spacing: 1.1px;
          line-height: 15px;
          color: $widget-legend-color;
          text-transform: uppercase;
          height: 30px;
          &:hover {
            cursor: pointer;
          }
        }
        &__content {
          margin-top: -10px;
          position: relative;
          z-index: 5;
          display: flex;
          &__list {
            overflow: hidden;
            align-items: center;
            flex-direction: row;
            display: flex;
            justify-content: flex-start;
            .tech-section {
              justify-content: center;
              height: 21px;
              border-radius: 4px;
              background: $technology-background-color;
              text-align: center;
              margin-right: 12px;
              margin-bottom: 8px;
              padding: 0 6px;
              max-width: 119px;
              &:hover {
                cursor: pointer;
              }
              .tech {
                color: $technology-color;
                font-family: $default-application-font-regular;
                font-size: 14px;
                letter-spacing: 0;
                line-height: 17px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                text-transform: capitalize;
                &.vulnerable {
                  color: $technology-vulnerable-color;
                }
              }
              &.vulnerable {
                background-color: $technology-vulnerable-background-color;
              }
            }
          }
          &__display-toggle {
            width: 100px;
            background: $technology-list-toggle-background;
            position: absolute;
            top: 0;
            right: -25px;
            text-align: right;
            margin-right: 12px;
            margin-bottom: 8px;
            display: flex;
            justify-content: right;
            align-items: center;
            .hide-technologies-btn {
              right: 15px;
              color: var(--brand-primary);
              font-family: $default-application-font-regular;
              font-size: 14px;
              letter-spacing: 0;
              &:hover {
                cursor: pointer;
              }
            }
            &.open {
              position: unset;
            }
          }
        }
      }
      &__waf {
        min-width: 5%;
        max-width: 5%;
        margin-right: 1%;
        display: flex;
        flex-direction: column;
        align-items: center;
        .header {
          position: absolute;
          top: 100px;
          width: 3%;
          margin-left: -1px;
          display: flex;
          justify-content: center;
          align-items: center;
          font-family: $default-application-font-regular;
          font-size: 12px;
          letter-spacing: 1.1px;
          line-height: 15px;
          color: $widget-legend-color;
          text-transform: uppercase;
          height: 30px;
          &:hover {
            cursor: pointer;
          }
        }
        .content {
          color: var(--text-primary);
          font-family: $default-application-font-regular;
          display: flex;
          flex-direction: column;
          align-items: center;
          font-size: 14px;
          margin-top: 19px;
          .waf-icon-indication {
            .icon {
              fill: black;
              &.waf-icon {
                z-index: 5;
                &.lite {
                  fill: $technology-vulnerable-color;
                }
                fill: $technology-color;
                width: 28px;
              }
              &.not-waf-icon {
                z-index: 1;
                fill: $widget-no-delta-text-color;
                width: 28px;
                margin-top: -15px;
              }
            }
            .spinner {
              margin-top: -71px;
              margin-left: -17px;
            }
          }
          .waf-vendor {
            font-family: $default-application-font-regular;
            font-size: x-small;
            margin-top: 7px;
            text-align: center;
          }
        }
      }
      &__hosts {
        min-width: 22%;
        max-width: 22%;
        display: flex;
        flex-direction: column;
        .header {
          display: flex;
          align-items: center;
          position: absolute;
          top: 100px;
          font-family: $default-application-font-regular;
          font-size: 12px;
          letter-spacing: 1.1px;
          line-height: 15px;
          color: $widget-legend-color;
          text-transform: uppercase;
          height: 30px;
        }
        .content {
          color: var(--text-primary);
          font-family: $default-application-font-regular;
          display: flex;
          flex-direction: column;
          align-items: center;
          font-size: 14px;
          .item-value {
            z-index: 5;
            width: 100%;
            display: flex;
            flex-direction: column;
            line-height: 50px;
          }
          .hosts-ports-divider {
            opacity: 0.5;
          }
          .material-icons-outlined {
            &.show-all-hosts {
              font-size: 28px;
              color: var(--text-secondary);
              transform: rotate(45deg);
            }
            &:hover {
              cursor: pointer;
            }
          }
          .show-all-hosts-tooltip {
            position: relative;
            left: 185px;
            background-color: transparent;
          }
        }
      }
      &__last-scanned {
        min-width: 8%;
        max-width: 8%;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-left: 2%;
        .header {
          position: absolute;
          top: 100px;
          width: 8%;
          margin-left: -3px;
          display: flex;
          justify-content: center;
          align-items: center;
          font-family: $default-application-font-regular;
          font-size: 12px;
          letter-spacing: 1.1px;
          line-height: 15px;
          color: $widget-legend-color;
          text-transform: uppercase;
          height: 30px;
          &:hover {
            cursor: pointer;
          }
        }
        .content {
          color: var(--text-primary);
          font-family: $default-application-font-regular;
          display: flex;
          flex-direction: column;
          align-items: center;
          font-size: 14px;
        }
      }
      .add-test-btn-txt {
        background-color: transparent;
        box-shadow: none;
        color: var(--brand-primary);
        font-family: $default-application-font-medium;
        font-size: rem(13);
        letter-spacing: 1.1px;
        line-height: $font-size-md;
        text-transform: uppercase;
      }
      .add-test-btn {
        background-color: $action-button-background-color;
        text-align: center;
        box-shadow: none;
        min-width: rem(28);
        max-width: rem(28);
        min-height: rem(28);
        max-height: rem(28);
        user-select: none;
        padding: 0;
        color: $action-button-text-color;
        .v-icon {
          font-size: rem(18);
        }
        &:hover {
          cursor: pointer;
          border: $action-button-hover-border;
          background-color: $action-button-hover-background-color;
        }
        &:active{
          border: $action-button-active-border;
          background-color: $action-button-hover-background-color;
        }
        &:before{
          color: transparent;
        }

      }
      .open-btn {
        background-color: $action-button-background-color;
        box-shadow: none;
        min-width: rem(28);
        max-width: rem(28);
        min-height: rem(28);
        max-height: rem(28);
        user-select: none;
        color: $action-button-text-color;
        .v-icon {
          font-size: rem(18);
        }
        &:hover {
          cursor: pointer;
          border: $action-button-hover-border;
          background-color: $action-button-hover-background-color;
        }
        &:active{
          border: $action-button-active-border;
          background-color: $action-button-hover-background-color;
        }
        &:before{
          color: transparent;
        }
      }

      .add-test-icon {
        color: var(--brand-primary);
        font-size: 22px;
        margin-right: 3px;
        &-no-txt{
          margin-right: 0;
        }
      }

      .main-info {
        align-items: center;
        display: flex;
        min-height: 150px;
        .sensitivity-tooltip {
          margin-left: -10px;
          margin-right: 10px;
          margin-top: -5px;
        }
        &__sensitivity-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          .header {
            position: absolute;
            top: 100px;
            width: 1%;
            margin-left: -20px;
            display: flex;
            justify-content: center;
            align-items: center;
            font-family: $default-application-font-regular;
            font-size: 12px;
            letter-spacing: 1.1px;
            line-height: 15px;
            color: $widget-legend-color;
            text-transform: uppercase;
            height: 30px;
            &:hover {
              cursor: pointer;
            }
          }
          &__data {
            .sensitivity {
              width: 7px;
              height: 50px;
              border-radius: 12px;
              &.critical {
                background-color: $issue-severity-blocker-color;
                opacity: 0.8;
              }
              &.high {
                background-color: $issue-severity-high-color;
                opacity: 0.8;
              }
              &.medium {
                background-color: $issue-severity-medium-color;
                opacity: 0.8;
              }
              &.low {
                background-color: $issue-severity-low-color;
                opacity: 0.8;
              }
              &.informative {
                background-color: $issue-severity-informative-color;
                opacity: 0.8;
              }
              &:hover {
                cursor: pointer;
              }
            }
          }
        }
      }
      .site {
        min-width: 15%;
        max-width: 15%;
        margin-right: 2%;
        margin-top: -5px;
        display: flex;
        align-items: center;
        flex-direction: column;
        .material-icons-round {
          &.successful {
            &:hover {
              cursor: auto;
            }
          }
        }
        .header {
          display: flex;
          align-items: center;
          position: absolute;
          top: 100px;
          margin-left: 3%;
          width: 15%;
          font-family: $default-application-font-regular;
          font-size: 12px;
          letter-spacing: 1.1px;
          line-height: 15px;
          color: $widget-legend-color;
          text-transform: uppercase;
          height: 30px;
          &:hover {
            cursor: pointer;
          }
        }
        .content {
          align-items: center;
          cursor: pointer;
          display: flex;
          font-size: 14px;
          line-height: 20px;
          width: 100%;
          margin-right: 20px;
          height: 45px;

          .new-item {
            width: 70%;
            margin-left: 0;
            margin-right: 10px;
            .tooltip-badge-container {
              display: flex;
              .ssl-indicator {
                margin-bottom: 4px;
                margin-left: -1px;
                color: var(--ssl-indicator-color, $severity-low-text-color);
              }
              .url-title {
                max-width: 100%;
                align-self: center;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                margin-left: 6px;
                display: block;
                color: var(--text-primary);
                font-family: $default-application-font-medium;
                font-weight: 600;

                &.successful {
                  font-weight: 500;
                }
              }

              .v-badge {
                line-height: 4;
                max-width: 100%;
              }

              :deep(.v-badge__badge) {
                background: transparent !important;
              }

              .badges-wrapper {
                display: flex;
                flex-direction: column;
                align-content: flex-start;
                margin-left: 5px;

                .new-icon {
                  width: 24px !important;
                  fill: $new-asset-badge-color;
                  background: transparent;
                  transform: rotate(26deg);
                }

                .manual-config-badge {
                  color: $default-button-color;
                  font-size: 16px;
                  margin-left: var(--manual-offset-x);
                  margin-top: var(--manual-offset-y);
                  transform: rotate(180deg);
                }
              }
            }
          }

          .copy {
            margin-right: rem(-11);
            margin-left: 3%;
            .copy-btn {
              background-color: $action-button-background-color;
              box-shadow: none;
              letter-spacing: 1.07px;
              line-height: $font-size-md;
              text-align: center;
              text-transform: uppercase;
              min-width: rem(28);
              max-width: rem(28);
              min-height: rem(28);
              max-height: rem(28);
              color: $action-button-text-color;
              .v-icon {
                font-size: $font-size-md;
              }
              &:hover {
                cursor: pointer;
                border: $action-button-hover-border;
                background-color: $action-button-hover-background-color;
              }
              &:active{
                border: $action-button-active-border;
                background-color: $action-button-hover-background-color;
              }
            }
          }
          .sitemap {
            margin-right: rem(-40);
            padding-left: rem(18);
            .sitemap-btn {
              background-color: $action-button-background-color;
              box-shadow: none;
              letter-spacing: 1.07px;
              line-height: $font-size-md;
              text-align: center;
              text-transform: uppercase;
              min-width: rem(28);
              max-width: rem(28);
              min-height: rem(28);
              max-height: rem(28);
              color: $action-button-text-color;
              .v-icon {
                font-size: $font-size-md;
              }
              &:hover {
                cursor: pointer;
                border: $action-button-hover-border;
                background-color: $action-button-hover-background-color;
              }
              &:active{
                border: $action-button-active-border;
                background-color: $action-button-hover-background-color;
              }
            }
          }
          .url {
            width: 70%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .v-progress-circular {
            margin-right: 10px;
          }

          .shield-icon {
            font-size: 20px;
            margin-right: 10px;
          }

          .more-btn {
            opacity: 0;
            color: var(--text-disabled);
            &:hover {
              cursor: pointer;
            }
          }

          &.successful {
            font-weight: 500;
          }

          .status-icon {
            width: 22px;
            margin-left: 5px;
          }
          .no-poc-status-icon {
            margin-right: $space-2;
            path:first-of-type {
              fill: $no-poc-icon-color;
            }
          }
        }
      }

      .host, .asset-name, .ports, .action {
        align-items: center;
        display: flex;
        flex: 1;
        min-height: 32px;

        .edit-item {
          width: 100px;
        }

        &.host {
          color: var(--text-primary);
          font-family: $default-application-font-regular;
          font-size: 14px;
          line-height: 20px;

          &.successful {
            font-weight: 500;
          }

          span {
            color: var(--status-error);
            font-size: 12px;
            font-weight: normal;
            letter-spacing: 0;
            line-height: 18px;
            margin-left: 15px;
          }

          img {
            margin-right: 10px;
          }
        }

        &.asset-name, &.hosts {
          align-items: center;
          color: var(--text-primary);
          display: flex;
          font-family: $default-application-font-regular;
          font-size: 14px;
          line-height: 16px;
          justify-content: space-between;
        }

        &.action {
          display: flex;
          justify-content: space-between;
        }

        .edit-ports {
          border-radius: 2px;
          cursor: pointer;
          display: flex;
          margin-right: 80px;
        }
      }
    }
    &__tags {
      display: flex;
      align-self: end;
      align-items: center;
      &__tags-list {
        margin-right: 10px;
        .tag, .auto-tag {
          margin-right: 8px;
        }
      }
      .no-tags {
        margin-right: 5px;
        color:$widget-legend-color;
        font-family: $default-application-font-regular;
        font-size: 12px;
        letter-spacing: 1.1px;
        line-height: 15px;
      }
      .material-icons {
        color: $default-button-color;
        font-size: 18px;
        &:hover {
          cursor: pointer;
          color: $default-button-hover-color;
        }
      }
    }
  }

  .expand-icon {
    position: absolute;
    right: 2%;
  }
}

#row:hover #more {
  opacity: 1;
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.configuration-item {
  .v-chip.v-chip--outlined.v-chip.v-chip {
    background-color: $tag-chip-background-color;
    border-color: transparent;
    padding: $space-1;
    font-size: $font-size-sm;
  }
  .v-chip.v-chip--outlined .v-icon {
    font-size: $font-size-base;
    color: var(--text-primary);
  }
  .v-chip--clickable{
    &:active{
      box-shadow: none;
    }
  }
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.configuration-item {
  .checkbox-container {
    margin-bottom: rem(40);
  }
  &__content {
    &__info {
      .main-info {
        .site {
          .v-badge__badge .v-icon {
            height: rem(35);
            width: rem(40);
          }
          .v-badge__badge {
            background: none;
          }
          .v-badge {
            background: none;
            border: none;
          }
        }
      }
    }
  }
}
.suppress-subdomains-popup {
  .v-dialog {
    overflow-y: hidden;
  }
}
</style>
